/**
 * The conversation, as on the old kiosk: the newest message on top, at eye height, older ones fading
 * downwards. The assistant's words are bold, the user's on a bubble in the accent, a touch too (with a
 * hand, as a spoken turn has a microphone), notices in the middle. While an answer is coming, a bubble with three dots waits on the
 * right of the assistant's column, where a full answer ends; then the answer takes its place without rising (conversation.ts
 * `fillsDots`). While the user speaks, the user's bubble has the dots.
 * In dev mode each answer shows how it was produced and opens its trace.
 */
import { useEffect, useRef, useState } from 'react';
import { isThinking, type AssistantEntry, type ChatEntry } from '../../core/conversation';
import { useAppearance, useConversation, useDevMode, useMode, useTranslate } from '../../react/hooks';
import { useVoice } from '../../voice/useVoice';
import { Icon } from '../../ui-kit/Icon';
import { ThinkingDots } from '../../ui-kit/ThinkingDots';
import { StateView } from '../../devtools/StateView';
import { TraceView, replyChips } from '../../devtools/TraceView';
import { changedCount, turnStateOf } from '../../devtools/turn-state';
import css from './Chat.module.css';

export function ChatModule() {
  const entries = useConversation((c) => c.entries);
  const thinking = useConversation(isThinking);
  const activity = useVoice((v) => v.activity);
  const mode = useMode();
  const dev = useDevMode();
  const { touchPills } = useAppearance();
  const shown = touchPills ? entries : entries.filter((e) => !(e.role === 'user' && e.source === 'touch'));
  const scroller = useRef<HTMLDivElement>(null);

  // The user's dots stay from their first word until their words take the dots' place: the words come once the
  // speech is transcribed, after it ends. Nothing transcribed (`processing_end`): the dots go.
  const said = entries.filter((e) => e.role === 'user').length;
  const [saidBefore, setSaidBefore] = useState<number | null>(null);
  if (activity === 'listening' && saidBefore !== said) setSaidBefore(said);
  const listening = activity === 'listening' || (activity === 'processing' && saidBefore === said);

  // The newest is on top: bring the view back there (only the chat scrolls, never the scaled canvas).
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [entries.length, thinking, listening]);

  return (
    <div className={`${css.chat} ${mode.behaviour.captions === 'large' ? css.large : ''}`} role="log" aria-live="polite">
      <div ref={scroller} className={css.scroll} tabIndex={0}>
        {thinking && <div className={css.row}><div className={css.waiting}><div className={`${css.bubble} ${css.agent} ${css.delayed}`}><ThinkingDots /></div></div></div>}
        {listening && <div className={css.row}><div className={`${css.bubble} ${css.user}`}><ThinkingDots /></div></div>}
        {shown.slice().reverse().map((e) => <Entry key={e.id} entry={e} dev={dev} />)}
      </div>
    </div>
  );
}

function Entry({ entry, dev }: { entry: ChatEntry; dev: boolean }) {
  const t = useTranslate();
  switch (entry.role) {
    case 'user':
      return (
        <div className={`${css.row} ${entry.source === 'voice' ? css.fills : ''}`}>
          <div className={`${css.bubble} ${css.user} ${entry.status === 'sending' ? css.sending : ''} ${entry.status === 'failed' ? css.failed : ''}`}>
            {entry.source === 'voice' && <Icon name="mic" size={20} className={css.sourceIcon} />}
            {entry.source === 'touch' && <Icon name="hand" size={20} className={css.sourceIcon} />}
            {entry.text}
            {entry.status === 'failed' && <span className={css.failedNote}>{t('chat.not_sent')}</span>}
          </div>
        </div>
      );
    case 'assistant':
      return <AssistantBubble entry={entry} dev={dev} />;
    case 'notice':
      return (
        <p className={`${css.notice} ${css[entry.tone]}`}>
          {entry.label && <strong>{t(entry.label)}: </strong>}
          {entry.text || (entry.key ? t(entry.key) : '')}
        </p>
      );
  }
}

/** In dev (or with «Debug sotto i messaggi»): how it was produced, `{ }` its steps, the counter the state it left. */
function AssistantBubble({ entry, dev }: { entry: AssistantEntry; dev: boolean }) {
  const t = useTranslate();
  const [open, setOpen] = useState<'steps' | 'state' | null>(null);
  const chips = dev ? replyChips(entry.message) : [];
  const state = dev ? turnStateOf(entry.message) : null;
  const changed = state ? changedCount(state) : 0;
  const toggle = (part: 'steps' | 'state') => setOpen((o) => (o === part ? null : part));
  return (
    <div className={`${css.row} ${entry.fillsDots ? css.fills : ''}`}>
      <div className={`${css.bubble} ${css.agent}`}>
        {entry.text}
        {dev && (
          <div className={css.chips}>
            {chips.map((c) => <span key={c} className={css.chip}>{c}</span>)}
            <button className={`${css.chip} ${open === 'steps' ? css.chipOn : ''}`} onClick={() => toggle('steps')} aria-label={t('debug.steps')}>{'{ }'}</button>
            {state && (
              <button className={`${css.chip} ${css.count} ${changed ? css.moved : ''} ${open === 'state' ? css.chipOn : ''}`}
                onClick={() => toggle('state')} aria-label={t('debug.changed', { n: changed })} title={t('debug.changed', { n: changed })}>
                {changed}
              </button>
            )}
          </div>
        )}
        {dev && open === 'steps' && <TraceView message={entry.message} parts={['steps']} />}
        {dev && open === 'state' && state && <StateView state={state} />}
      </div>
    </div>
  );
}
